import React from "react";
import "./OrderBook.css";

function OrderBook({ marketData, symbol, userOrders = [] }) {
  // Now use marketData directly instead of marketData[symbol]
  const buyLevels = marketData?.levels?.buy || [];
  const sellLevels = marketData?.levels?.sell || [];

  // Check if we have any data
  const hasBuyData = Array.isArray(buyLevels) && buyLevels.length > 0;
  const hasSellData = Array.isArray(sellLevels) && sellLevels.length > 0;

  // Transform the array format [price, volume] into objects for easier handling
  const formattedBuys = hasBuyData
    ? buyLevels.map((level) => {
        const [price, volume] = level;
        return {
          price: price,
          volume: volume,
        };
      })
    : [];

  const formattedSells = hasSellData
    ? sellLevels.map((level) => {
        const [price, volume] = level;
        return {
          price: price,
          volume: volume,
        };
      })
    : [];

  // Find the maximum volume for visualization
  const maxVolume = Math.max(
    ...formattedBuys.map((level) => level.volume || 0),
    ...formattedSells.map((level) => level.volume || 0),
    1 // Default to 1 if all arrays are empty
  );

  // Calculate the spread
  const spread =
    formattedSells.length && formattedBuys.length
      ? Math.round(
          Math.min(...formattedSells.map((level) => level.price)) -
            Math.max(...formattedBuys.map((level) => level.price))
        )
      : "N/A";

  // Build sets of prices where the user has active orders
  const myBuyPrices = new Set(
    userOrders.filter((o) => o.side === "buy").map((o) => Math.round(o.price))
  );
  const mySellPrices = new Set(
    userOrders.filter((o) => o.side === "sell").map((o) => Math.round(o.price))
  );

  return (
    <div className="order-book">
      <h2 className="order-book-title">Order Book</h2>

      <div className="order-book-debug">
        <div>Buy levels: {hasBuyData ? buyLevels.length : "None"}</div>
        <div>Sell levels: {hasSellData ? sellLevels.length : "None"}</div>
      </div>

      <div className="order-book-container">
        <div className="order-book-header">
          <div className="price-col">Price</div>
          <div className="size-col">Size</div>
        </div>

        <div className="asks-container">
          {formattedSells
            .slice()
            .reverse()
            .map((level, index) => (
              <div key={`ask-${index}`} className={`order-row ask-row${mySellPrices.has(Math.round(level.price)) ? " my-order" : ""}`}>
                <div className="price-col sell-price">
                  ${Math.round(level.price)}
                </div>
                <div className="size-col">{Math.round(level.volume)}</div>
                <div
                  className="volume-viz sell-viz"
                  style={{
                    width: `${(level.volume / maxVolume) * 100}%`,
                  }}
                />
              </div>
            ))}

          {!hasSellData && (
            <div className="no-data-message">No sell orders available</div>
          )}
        </div>

        <div className="spread">
          <span>Spread: {spread !== "N/A" ? `$${spread}` : "N/A"}</span>
        </div>

        <div className="bids-container">
          {formattedBuys.map((level, index) => (
            <div key={`bid-${index}`} className={`order-row bid-row${myBuyPrices.has(Math.round(level.price)) ? " my-order" : ""}`}>
              <div className="price-col buy-price">
                ${Math.round(level.price)}
              </div>
              <div className="size-col">{Math.round(level.volume)}</div>
              <div
                className="volume-viz buy-viz"
                style={{
                  width: `${(level.volume / maxVolume) * 100}%`,
                }}
              />
            </div>
          ))}

          {!hasBuyData && (
            <div className="no-data-message">No buy orders available</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrderBook;
